import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, ChevronDown, ChevronRight, WifiOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEffectiveRole } from '../../hooks/useEffectiveRole';
import { apiCall } from '../../services/api.service';
import { config } from '../../config';
import { UserSessionRegistry } from '../../stores/user-session.registry';
import { ConnectionStatusStore } from '../../stores/connection-status.store';
import { emitAuthFailure } from '../../services/authEvents';
import { toastMention } from '../../features/clu/utils/notify';

interface Notification {
  id: string;
  type: 'ALERT' | 'NEWS' | 'TASK' | 'APPROVAL';
  severity: 'INFO' | 'WARN' | 'CRITICAL';
  title: string;
  body: string;
  status: 'PENDING' | 'SENT' | 'FAILED' | 'ACKED' | 'EXPIRED';
  /** Why this notification exists. Only 'mention' interrupts. */
  reason?: string | null;
  created_at: string;
  metadata?: {
    dedup_key?: string;
    event_type?: string;
    /** An in-app route to jump to directly (e.g. `clu_ng/router.py`'s
     *  console_invite sets this to the invited session's own chat URL).
     *  When present, a click goes straight there instead of the generic
     *  notification-detail page — see handleNotificationClick below. */
    deep_link?: string;
    [key: string]: any;
  };
}

interface SeverityCounts {
  critical: number;
  warn: number;
  info: number;
  total: number;
  /** Unread mentions specifically — the only kind allowed to raise a toast
   *  or claim the tab title. */
  mentions: number;
}

interface StandaloneItem {
  kind: 'standalone';
  notification: Notification;
}

interface GroupItem {
  kind: 'group';
  key: string;
  items: Notification[];
  count: number;
  representative: Notification;
}

type DisplayItem = StandaloneItem | GroupItem;

interface NotificationBellProps {
  className?: string;
}

const severityOrder: Record<string, number> = { CRITICAL: 0, WARN: 1, INFO: 2 };

const canGroup = (n: Notification): boolean => !!n.metadata?.dedup_key;

function buildGroups(sortedNotifications: Notification[]): DisplayItem[] {
  const groupMap = new Map<string, number>();
  const result: DisplayItem[] = [];

  for (const notification of sortedNotifications) {
    if (!canGroup(notification)) {
      result.push({ kind: 'standalone', notification });
      continue;
    }

    const key = notification.metadata!.dedup_key!;
    const existingIndex = groupMap.get(key);

    if (existingIndex !== undefined) {
      const group = result[existingIndex] as GroupItem;
      group.items.push(notification);
      group.count = group.items.length;
    } else {
      const newIndex = result.length;
      groupMap.set(key, newIndex);
      result.push({
        kind: 'group',
        key,
        items: [notification],
        count: 1,
        representative: notification,
      });
    }
  }

  return result.map((item) => {
    if (item.kind === 'group' && item.count === 1) {
      return { kind: 'standalone' as const, notification: item.items[0] };
    }
    return item;
  });
}

type SeverityTab = 'ALL' | 'CRITICAL' | 'WARN' | 'INFO';

const NotificationBell: React.FC<NotificationBellProps> = ({ className = '' }) => {
  const [severityCounts, setSeverityCounts] = useState<SeverityCounts>({ critical: 0, warn: 0, info: 0, total: 0, mentions: 0 });
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<SeverityTab>('ALL');
  const [pushEnabled, setPushEnabled] = useState<boolean>(
    () => localStorage.getItem('clu-push-notifications-enabled') === 'true'
  );
  const [pushPermission, setPushPermission] = useState<NotificationPermission>(
    () => ('Notification' in window ? window.Notification.permission : 'denied')
  );
  const pushEnabledRef = useRef<boolean>(
    localStorage.getItem('clu-push-notifications-enabled') === 'true'
  );
  const { activeOrganization } = useAuth();
  const { effectiveRole } = useEffectiveRole();
  const navigate = useNavigate();
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const lastUnreadFetchAtRef = useRef<number>(0);
  const unreadFetchInFlightRef = useRef<boolean>(false);
  const sseAbortRef = useRef<AbortController | null>(null);
  const sseReconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sseReconnectDelayRef = useRef<number>(3000);
  const sseIntendedRef = useRef<boolean>(false);
  const sseConnectRef = useRef<() => void>(() => {});
  const sseConsecutive403Ref = useRef<number>(0);
  const ssePollFallbackTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Set when the server gave this tab's slot to a newer one. Reconnecting
  // would take the slot straight back off whichever tab just got it, and the
  // two would trade it forever — a reconnect storm caused by the very limit
  // meant to prevent one. So this tab waits to be looked at instead.
  const sseStandbyRef = useRef<boolean>(false);
  const [streamStandby, setStreamStandby] = useState<{ limit?: number } | null>(null);
  const SSE_MAX_CONSECUTIVE_403 = 3;
  const SSE_POLL_FALLBACK_INTERVAL_MS = 60000;
  const originalTitleRef = useRef<string>(document.title);

  const unreadCount = severityCounts.total;

  const getBadgeCount = (): number => {
    if (severityCounts.critical > 0) return severityCounts.critical;
    if (severityCounts.warn > 0) return severityCounts.warn;
    return severityCounts.info;
  };

  const getBadgeColor = (): string => {
    if (severityCounts.critical > 0) return 'bg-red-600';
    if (severityCounts.warn > 0) return 'bg-amber-500';
    return 'bg-gray-400';
  };

  const getBellColor = (): string => {
    if (severityCounts.critical > 0) return 'text-red-600 dark:text-red-300';
    if (severityCounts.warn > 0) return 'text-amber-500 dark:text-amber-400';
    return 'text-ink';
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // The tab title is the only channel that reaches somebody whose tab is in
  // the background — a toast is invisible to them by definition. Mentions win
  // over CRITICAL because a mention is addressed to this person by name and a
  // CRITICAL alert is not necessarily.
  useEffect(() => {
    if (severityCounts.mentions > 0) {
      document.title = `(${severityCounts.mentions}) GRID`;
    } else if (severityCounts.critical > 0) {
      document.title = `(${severityCounts.critical}) GRID`;
    } else {
      document.title = originalTitleRef.current;
    }
    return () => {
      document.title = originalTitleRef.current;
    };
  }, [severityCounts.mentions, severityCounts.critical]);

  const fetchUnreadCount = useCallback(async () => {
    const now = Date.now();
    if (unreadFetchInFlightRef.current) return;
    if (now - lastUnreadFetchAtRef.current < 5000) return;
    lastUnreadFetchAtRef.current = now;
    unreadFetchInFlightRef.current = true;
    try {
      // No agent_id filter: the badge counts everything this person can see,
      // matching the stream they are listening on. Scoping it to the selected
      // tenant while the stream is user-wide would deliver a mention live and
      // then not count it.
      const data = await apiCall('notifications/unread-count', 'GET', null, false, {
        shortTimeout: 10000,
        silentError: true,
      });
      setSeverityCounts({
        critical: data.critical ?? 0,
        warn: data.warn ?? 0,
        info: data.info ?? 0,
        total: data.count || 0,
        mentions: data.mentions ?? 0,
      });
    } catch {
      // connection health is managed exclusively by ConnectionStatusStore's /health poller
    } finally {
      unreadFetchInFlightRef.current = false;
    }
  }, [activeOrganization?.organization_id]);

  const scheduleReconnect = useCallback(() => {
    if (!sseIntendedRef.current) return;
    if (sseStandbyRef.current) return;
    if (!ConnectionStatusStore.getState().reachable) return;
    sseReconnectTimerRef.current = setTimeout(() => {
      sseReconnectDelayRef.current = Math.min(sseReconnectDelayRef.current * 2, 30000);
      sseConnectRef.current();
    }, sseReconnectDelayRef.current);
  }, []);

  const setPushEnabledPersisted = (value: boolean) => {
    pushEnabledRef.current = value;
    setPushEnabled(value);
    localStorage.setItem('clu-push-notifications-enabled', value ? 'true' : 'false');
  };

  const requestPushPermission = async () => {
    if (!('Notification' in window)) return;
    const result = await window.Notification.requestPermission();
    setPushPermission(result);
    if (result === 'granted') {
      setPushEnabledPersisted(true);
    }
  };

  const showMentionToast = (msg: Notification) => {
    // `body` is the message that named this person, capped server-side. The
    // title already says who, so the toast shows the sentence rather than
    // repeating the name.
    const who = msg.metadata?.sender_name || msg.metadata?.sender_email || 'Someone';
    const deepLink = msg.metadata?.deep_link;
    toastMention(
      String(who),
      msg.body || '',
      typeof deepLink === 'string' && deepLink ? () => navigate(deepLink) : undefined,
    );
  };

  const sendPushNotification = (msg: Notification) => {
    if (!('Notification' in window)) return;
    if (window.Notification.permission !== 'granted') return;
    if (!pushEnabledRef.current) return;

    const lastPushedId = localStorage.getItem('clu-push-last-notif-id');
    if (lastPushedId === msg.id) return;
    localStorage.setItem('clu-push-last-notif-id', msg.id);

    new window.Notification(`🔴 ${msg.title}`, {
      body: msg.body,
      icon: 'https://pub-1d2dcf0b6ba7499db01410ad196bb3a2.r2.dev/logo%20CLU%20isotipo%20transaparente.png',
      tag: msg.id,
    });
  };

  const stopPollFallback = useCallback(() => {
    if (ssePollFallbackTimerRef.current) {
      clearInterval(ssePollFallbackTimerRef.current);
      ssePollFallbackTimerRef.current = null;
    }
  }, []);

  const startPollFallback = useCallback(() => {
    if (ssePollFallbackTimerRef.current) return;
    ssePollFallbackTimerRef.current = setInterval(() => {
      fetchUnreadCount();
    }, SSE_POLL_FALLBACK_INTERVAL_MS);
  }, [fetchUnreadCount]);

  // Stop holding a live stream and keep the badge alive by polling instead.
  // Stated, not silent: this used to fall into the generic retry path, so the
  // only symptom was that notifications quietly stopped arriving.
  const enterStandby = useCallback((limit?: number) => {
    sseStandbyRef.current = true;
    setStreamStandby({ limit });
    if (sseAbortRef.current) {
      sseAbortRef.current.abort();
      sseAbortRef.current = null;
    }
    if (sseReconnectTimerRef.current) {
      clearTimeout(sseReconnectTimerRef.current);
      sseReconnectTimerRef.current = null;
    }
    startPollFallback();
  }, [startPollFallback]);

  const leaveStandby = useCallback(() => {
    if (!sseStandbyRef.current) return;
    sseStandbyRef.current = false;
    setStreamStandby(null);
    sseReconnectDelayRef.current = 3000;
    if (sseIntendedRef.current) sseConnectRef.current();
  }, []);

  const connectSse = useCallback(() => {
    if (!sseIntendedRef.current) return;
    if (sseStandbyRef.current) return;

    // One stream per PERSON, not per selected tenant. The old URL carried
    // `agent_id` from the header, so a mention raised in any other agent this
    // person works in never arrived live — which for a mention, whose point is
    // to reach somebody who is not in the room, meant not arriving at all.
    // Scoping is enforced server-side against the same allow list the list
    // endpoint uses; nothing cross-tenant reaches this connection.
    const base = config.apiUrl || window.location.origin;
    const url = `${base}/api/notifications/stream/me`;

    if (sseAbortRef.current) sseAbortRef.current.abort();
    const controller = new AbortController();
    sseAbortRef.current = controller;

    const run = async () => {
      try {
        const headers = await UserSessionRegistry.getAuthHeaders();
        const resp = await fetch(url, {
          signal: controller.signal,
          headers: { ...headers, Accept: 'text/event-stream', 'Cache-Control': 'no-cache' },
        });

        if (!resp.ok || !resp.body) {
          if (resp.status === 429) {
            // This account is holding as many live streams as the server will
            // carry. Retrying on a timer cannot fix it — only closing a tab
            // can — so back off to polling and say so, instead of looping
            // against a condition that does not resolve by waiting.
            let limit: number | undefined;
            try {
              const body = await resp.json();
              limit = body?.detail?.limit ?? body?.limit;
            } catch { /* the number is a nicety, the degradation is not */ }
            enterStandby(typeof limit === 'number' ? limit : undefined);
            return;
          }

          if (resp.status === 403) {
            try {
              const body = await resp.json();
              const code = body?.detail?.error_code ?? body?.detail?.error ?? body?.error_code ?? body?.error;
              if (code === 'inactive' || code === 'expired') {
                emitAuthFailure('notifications_stream_inactive_key');
                return;
              }
            } catch { /* ignore parse error — fall through below */ }

            sseConsecutive403Ref.current += 1;
            if (sseConsecutive403Ref.current >= SSE_MAX_CONSECUTIVE_403) {
              // Non-recoverable 403 (billing/org/agent-access denial) — retrying on a
              // timer forever just spams the backend. Stop the SSE loop and fall back
              // to slow polling so the badge count still updates.
              startPollFallback();
              return;
            }
          }
          scheduleReconnect();
          return;
        }

        sseConsecutive403Ref.current = 0;
        stopPollFallback();

        const stableTimer = setTimeout(() => {
          if (sseIntendedRef.current) sseReconnectDelayRef.current = 3000;
        }, 15000);

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop() ?? '';
            for (const part of parts) {
              if (part.startsWith(':')) continue;
              const dataLine = part.match(/^data: (.+)$/ms)?.[1];
              if (!dataLine) continue;
              try {
                const msg = JSON.parse(dataLine);
                const eventLine = part.match(/^event: (.+)$/m)?.[1];
                if (eventLine === 'notification') {
                  const severity = msg?.severity as 'CRITICAL' | 'WARN' | 'INFO' | undefined;
                  const isMention = msg?.reason === 'mention';
                  setSeverityCounts((prev) => ({
                    critical: prev.critical + (severity === 'CRITICAL' ? 1 : 0),
                    warn: prev.warn + (severity === 'WARN' ? 1 : 0),
                    info: prev.info + (severity === 'INFO' ? 1 : 0),
                    total: prev.total + 1,
                    mentions: prev.mentions + (isMention ? 1 : 0),
                  }));
                  if (isMention) {
                    // The only notification allowed to interrupt. Everything
                    // else stays in the bell, which is where the noise would
                    // come from if this were opened up.
                    showMentionToast(msg as Notification);
                  }
                  if (severity === 'CRITICAL') {
                    sendPushNotification(msg as Notification);
                  }
                } else if (eventLine === 'stream_superseded') {
                  // A newer tab of this same account took this connection's
                  // slot. Deliberate on the server's part, so this is not a
                  // failure to retry — it is a handover to accept.
                  enterStandby(typeof msg?.limit === 'number' ? msg.limit : undefined);
                  return;
                } else if (eventLine === 'stats') {
                  // The pushed unread_count is per-AGENT and read state is
                  // per-person, so that number can no longer be believed —
                  // it counts notifications this viewer already read. Treat
                  // the event as "something changed" and ask for the real,
                  // caller-scoped count.
                  fetchUnreadCount();
                }
              } catch { /* ignore malformed */ }
            }
          }
        } finally {
          clearTimeout(stableTimer);
          reader.releaseLock();
        }

        if (sseIntendedRef.current) {
          scheduleReconnect();
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        if (sseIntendedRef.current) {
          scheduleReconnect();
        }
      }
    };

    run();
  }, [effectiveRole, scheduleReconnect, startPollFallback, stopPollFallback, enterStandby, activeOrganization?.organization_id]);

  sseConnectRef.current = connectSse;

  const fetchNotifications = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const data = await apiCall('notifications', 'GET', null, false, {
        params: { limit: 10, unread_only: true },
      });
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnreadCount();

    sseIntendedRef.current = true;
    sseReconnectDelayRef.current = 3000;
    sseConsecutive403Ref.current = 0;
    const connectTimer = setTimeout(() => {
      if (sseIntendedRef.current) connectSse();
    }, 50);

    // The tab being looked at is the one that should hold the live stream.
    // This is also what keeps the handover from oscillating: a superseded tab
    // comes back only when it becomes the foreground one, so the slots settle
    // on the tabs in use instead of being traded between all of them.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') leaveStandby();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    const handleStoreReconnect = () => {
      sseReconnectDelayRef.current = 3000;
      sseStandbyRef.current = false;
      setStreamStandby(null);
      if (sseIntendedRef.current) {
        sseConnectRef.current();
      }
      fetchUnreadCount();
    };

    ConnectionStatusStore.onReconnect(handleStoreReconnect);

    return () => {
      sseIntendedRef.current = false;
      sseStandbyRef.current = false;
      clearTimeout(connectTimer);
      document.removeEventListener('visibilitychange', handleVisibility);
      ConnectionStatusStore.offReconnect(handleStoreReconnect);
      if (sseReconnectTimerRef.current) {
        clearTimeout(sseReconnectTimerRef.current);
        sseReconnectTimerRef.current = null;
      }
      if (sseAbortRef.current) {
        sseAbortRef.current.abort();
        sseAbortRef.current = null;
      }
      stopPollFallback();
    };
  }, [activeOrganization?.organization_id, effectiveRole, stopPollFallback, leaveStandby]);

  useEffect(() => {
    if (showDropdown) {
      fetchNotifications();
      if (severityCounts.critical > 0) setActiveTab('CRITICAL');
      else if (severityCounts.warn > 0) setActiveTab('WARN');
      else setActiveTab('ALL');
    }
  }, [showDropdown, activeOrganization?.organization_id, effectiveRole]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleBellClick = () => setShowDropdown(!showDropdown);

  const handleNotificationClick = (notification: Notification) => {
    // 2026-09-03 finding: a colleague invited into a live CLU NG session got
    // a notification whose body carried the conversation's URL as plain
    // text — every click went to the generic /notifications/{id} detail
    // page regardless, so reaching the actual conversation meant reading
    // that text and copy-pasting the URL by hand. `metadata.deep_link` is
    // already written by producers that have a real destination (console_
    // invite's own chat URL, so far) — read it generically here so ANY
    // notification type gets a real one-click action just by setting it,
    // not only CLU NG's.
    const deepLink = notification.metadata?.deep_link;
    if (typeof deepLink === 'string' && deepLink) {
      navigate(deepLink);
    } else {
      navigate(`/notifications/${notification.id}`);
    }
    setShowDropdown(false);
  };

  const handleViewAll = () => {
    const qs = activeTab !== 'ALL' ? `?severity=${activeTab}` : '';
    navigate(`/notifications${qs}`);
    setShowDropdown(false);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return 'bg-red-600 text-white border-red-600';
      case 'WARN': return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-600';
      default: return 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-600';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'APPROVAL': return '✅';
      case 'ALERT': return '🚨';
      case 'TASK': return '📋';
      case 'NEWS': return '📢';
      default: return '🔔';
    }
  };

  const sortedNotifications = [...notifications].sort((a, b) => {
    const diff = (severityOrder[a.severity] ?? 3) - (severityOrder[b.severity] ?? 3);
    if (diff !== 0) return diff;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const displayItems = buildGroups(sortedNotifications);

  const filteredItems = activeTab === 'ALL'
    ? displayItems
    : displayItems.filter((item) => {
        const sev = item.kind === 'standalone'
          ? item.notification.severity
          : item.representative.severity;
        return sev === activeTab;
      });

  const previewItems = filteredItems.slice(0, 4);
  const hiddenCount = filteredItems.length - previewItems.length;

  const badgeCount = getBadgeCount();
  const badgeColor = getBadgeColor();
  const bellColor = getBellColor();

  const renderNotificationRow = (notification: Notification, indented: boolean = false) => {
    const isCritical = notification.severity === 'CRITICAL';
    const isWarn = notification.severity === 'WARN';
    return (
      <div
        key={notification.id}
        onClick={() => handleNotificationClick(notification)}
        className={`p-4 cursor-pointer transition-colors ${indented ? 'pl-8' : ''} ${
          isCritical
            ? 'border-l-4 border-red-600 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 hover:dark:bg-red-900/40'
            : isWarn
            ? 'border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 hover:dark:bg-amber-900/40'
            : 'hover:bg-surface-2'
        }`}
      >
        <div className="flex items-start space-x-3">
          <span className="text-2xl">{getTypeIcon(notification.type)}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <p className={`text-sm truncate ${isCritical ? 'font-semibold text-ink' : 'font-medium text-ink'}`}>
                {notification.title}
              </p>
              <span className={`ml-2 shrink-0 px-2 py-0.5 rounded text-xs border ${getSeverityColor(notification.severity)}`}>
                {notification.severity}
              </span>
            </div>
            <p className="text-sm text-ink-muted line-clamp-2">
              {notification.body}
            </p>
            <p className="text-xs text-ink-subtle mt-1">
              {new Date(notification.created_at).toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderGroupItem = (item: GroupItem) => {
    const { key, representative, items, count } = item;
    const isCritical = representative.severity === 'CRITICAL';
    const isWarn = representative.severity === 'WARN';
    const isExpanded = expandedGroups.has(key);

    return (
      <React.Fragment key={`group-${key}`}>
        <div
          onClick={() => handleNotificationClick(representative)}
          className={`p-4 cursor-pointer transition-colors ${
            isCritical
              ? 'border-l-4 border-red-600 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 hover:dark:bg-red-900/40'
              : isWarn
              ? 'border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 hover:dark:bg-amber-900/40'
              : 'hover:bg-surface-2'
          }`}
        >
          <div className="flex items-start space-x-3">
            <span className="text-2xl">{getTypeIcon(representative.type)}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <p className={`text-sm truncate ${isCritical ? 'font-semibold text-ink' : 'font-medium text-ink'}`}>
                  {representative.title}
                </p>
                <div className="flex items-center ml-2 shrink-0 gap-1">
                  <span className={`px-2 py-0.5 rounded text-xs border ${getSeverityColor(representative.severity)}`}>
                    {representative.severity}
                  </span>
                  <span className="bg-surface-2 text-ink-muted text-xs rounded-full px-2 py-0.5">
                    ×{count}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroup(key);
                    }}
                    className="flex items-center justify-center min-h-[44px] min-w-[44px] p-2 rounded hover:bg-gray-200 transition-colors text-ink"
                    aria-label={isExpanded ? 'Collapse group' : 'Expand group'}
                  >
                    {isExpanded ? (
                      <ChevronDown size={16} strokeWidth={2} />
                    ) : (
                      <ChevronRight size={16} strokeWidth={2} />
                    )}
                  </button>
                </div>
              </div>
              <p className="text-sm text-ink-muted line-clamp-2">
                {representative.body}
              </p>
              <p className="text-xs text-ink-subtle mt-1">
                {new Date(representative.created_at).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="border-l-2 border-line ml-4">
            {items.map((notification) => renderNotificationRow(notification, true))}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={handleBellClick}
        className="relative p-2 hover:text-ink focus:outline-none rounded-full hover:bg-surface-2 transition-colors"
        aria-label="Notifications"
        title={streamStandby ? 'Live updates paused in this tab — checking every minute' : undefined}
      >
        <Bell size={24} strokeWidth={2} className={unreadCount > 0 ? bellColor : 'text-ink-subtle'} />
        {unreadCount > 0 && (
          <span className={`absolute top-0 right-0 ${badgeColor} text-white text-xs font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center border-2 border-white leading-none`}>
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 top-12 bg-surface border border-line rounded-lg shadow-xl z-[10000] w-96 overflow-hidden flex flex-col" style={{ maxHeight: '520px' }}>
          <div className="p-4 border-b border-line shrink-0">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-ink">Notifications</h3>
              {unreadCount === 0 && !streamStandby && (
                <span className="text-xs text-ink-subtle">All read</span>
              )}
            </div>
            {streamStandby && (
              <div className="mb-3 flex items-start gap-2 rounded-md border border-line bg-surface-2 px-2.5 py-2">
                <WifiOff size={14} className="mt-0.5 shrink-0 text-ink-subtle" />
                <div className="text-xs text-ink-subtle">
                  <span className="font-medium text-ink">Live updates paused in this tab.</span>{' '}
                  {typeof streamStandby.limit === 'number'
                    ? `This account is using all ${streamStandby.limit} live connections, so a newer tab took this one's place.`
                    : "A newer tab of this account took this one's place."}{' '}
                  Counts still refresh every minute, and live updates come back when you return to this tab.
                </div>
              </div>
            )}
            {unreadCount > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {severityCounts.critical > 0 && (
                  <button
                    onClick={() => setActiveTab('CRITICAL')}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                      activeTab === 'CRITICAL'
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700 hover:bg-red-200'
                    }`}
                  >
                    🔴 {severityCounts.critical} Critical
                  </button>
                )}
                {severityCounts.warn > 0 && (
                  <button
                    onClick={() => setActiveTab('WARN')}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                      activeTab === 'WARN'
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700 hover:bg-amber-200'
                    }`}
                  >
                    🟡 {severityCounts.warn} Warn
                  </button>
                )}
                {severityCounts.info > 0 && (
                  <button
                    onClick={() => setActiveTab('INFO')}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                      activeTab === 'INFO'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    ⚪ {severityCounts.info} Info
                  </button>
                )}
                {(severityCounts.critical > 0 || severityCounts.warn > 0 || severityCounts.info > 0) && (
                  <button
                    onClick={() => setActiveTab('ALL')}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                      activeTab === 'ALL'
                        ? 'bg-gray-700 text-white border-gray-700'
                        : 'bg-surface-2 text-ink-muted border-line hover:bg-gray-200'
                    }`}
                  >
                    All {unreadCount}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-4 text-center text-ink-subtle">Loading...</div>
            ) : previewItems.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-sm text-ink-subtle">
                  {activeTab === 'ALL'
                    ? 'No pending notifications'
                    : `No ${activeTab.toLowerCase()} alerts`}
                </p>
                {activeTab !== 'ALL' && unreadCount > 0 && (
                  <button
                    onClick={() => setActiveTab('ALL')}
                    className="mt-2 text-xs text-blue-600 dark:text-blue-300 hover:underline"
                  >
                    View all {unreadCount}
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {previewItems.map((item) => {
                  if (item.kind === 'standalone') {
                    return renderNotificationRow(item.notification);
                  }
                  return renderGroupItem(item);
                })}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-line space-y-2 shrink-0">
            <button
              onClick={handleViewAll}
              className="w-full text-center text-sm text-blue-600 dark:text-blue-300 hover:text-blue-700 hover:dark:text-blue-300 font-medium py-2 hover:bg-blue-50 hover:dark:bg-blue-900/30 rounded transition-colors"
            >
              {hiddenCount > 0
                ? `See ${hiddenCount} more${activeTab !== 'ALL' ? ` ${activeTab.toLowerCase()}` : ''} alerts`
                : 'View all notifications'}
            </button>
            <div className="pt-2 border-t border-line space-y-1">
              {'Notification' in window && (() => {
                if (pushPermission === 'denied') {
                  return (
                    <div className="flex items-center gap-2 text-xs text-ink-subtle px-2 py-1.5">
                      <BellOff size={14} />
                      <span>Notifications blocked — enable in browser settings</span>
                    </div>
                  );
                }
                if (pushPermission === 'default') {
                  return (
                    <button
                      onClick={requestPushPermission}
                      className="w-full text-left text-xs text-ink-subtle hover:text-ink min-h-[44px] px-2 rounded hover:bg-surface-2 flex items-center gap-2 transition-colors"
                    >
                      <Bell size={14} />
                      <span>Enable critical alerts</span>
                    </button>
                  );
                }
                if (pushPermission === 'granted' && pushEnabled) {
                  return (
                    <button
                      onClick={() => setPushEnabledPersisted(false)}
                      className="w-full text-left text-xs text-ink-subtle hover:text-ink min-h-[44px] px-2 rounded hover:bg-surface-2 flex items-center gap-2 transition-colors"
                    >
                      <BellOff size={14} />
                      <span>Disable desktop alerts</span>
                    </button>
                  );
                }
                return (
                  <button
                    onClick={() => setPushEnabledPersisted(true)}
                    className="w-full text-left text-xs text-ink-subtle hover:text-ink min-h-[44px] px-2 rounded hover:bg-surface-2 flex items-center gap-2 transition-colors"
                  >
                    <Bell size={14} />
                    <span>Enable desktop alerts</span>
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
